// src/pages/About.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGraduationCap, FaBriefcase, FaCertificate, FaChevronDown } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';

/* ── Types ─────────────────────────────────────── */
type EntryType = 'education' | 'work' | 'certification';

interface TimelineEntry {
  date: string;
  title: string;
  description: string;
  technologies: string[];
  type: EntryType;
}

/* ── Data ──────────────────────────────────────── */
const entries: TimelineEntry[] = [
  {
    date: 'Aug 2025 — Jul 2026',
    title: 'Full-Stack Developer — Equans',
    description: 'Bouygues Energies & Services. Digitalization of processes within a design office and development of internal software.',
    technologies: ['VisionSoft', 'AI', 'JavaScript', 'Databases', 'APIs'],
    type: 'work',
  },
  {
    date: 'Sep 2023 — Aug 2025',
    title: "Master's — Blockchain Expert",
    description: "ESGI, Paris — Level 7 Certification. Decentralized systems, smart contract security, blockchain architecture.",
    technologies: ['Go', 'Solidity', 'Tezos', 'DevOps', 'Rust', 'React'],
    type: 'education',
  },
  {
    date: 'Sep 2023 — Aug 2025',
    title: 'Software Developer — Equans',
    description: 'Bouygues Energies & Services. Developing information & activity tracking software.',
    technologies: [],
    type: 'work',
  },
  {
    date: 'Jul 2023',
    title: 'TOEIC — 850/990',
    description: 'Upper-intermediate English certification.',
    technologies: [],
    type: 'certification',
  },
  {
    date: 'Jul 2023',
    title: "Bachelor's — Software & Network PM",
    description: 'ESGI — Level 6 Certification. Architecture, databases, cloud, project management.',
    technologies: ['C', 'PHP', 'JS', 'SQL', 'Git', 'Java', 'UML'],
    type: 'education',
  },
  {
    date: 'Feb — Aug 2023',
    title: 'Software Developer — Equans',
    description: 'Bouygues Energies & Services. Work-study position.',
    technologies: [],
    type: 'work',
  },
  {
    date: 'May — Jul 2022',
    title: 'Blockchain Developer Intern',
    description: 'NFT project development with Ethereum & Solidity.',
    technologies: ['Ethereum', 'Solidity', 'Web Dev'],
    type: 'work',
  },
  {
    date: 'Sep 2020 — Jul 2023',
    title: "Bachelor's — Blockchain Engineering",
    description: 'ESGI — 3-year program in Bitcoin/Ethereum, cryptography, and full-stack development.',
    technologies: ['Bitcoin', 'Ethereum', 'Docker', 'Linux', 'React', 'Node.js'],
    type: 'education',
  },
  {
    date: 'Jun — Jul 2021',
    title: 'IT Support Technician',
    description: 'Pathé Gaumont, Paris — End-user IT assistance and Active Directory management.',
    technologies: ['Active Directory'],
    type: 'work',
  },
  {
    date: 'Jul 2020',
    title: 'High School Diploma',
    description: 'Economics & Social Sciences — International Polish Mathematics — Honors. Lycée Montaigne, Paris.',
    technologies: [],
    type: 'education',
  },
];

/* ── Duration helper ───────────────────────────── */
const MONTH_IDX: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

const parseDuration = (dateStr: string): string | null => {
  const parts = dateStr.split(' — ');
  if (parts.length < 2) return null;

  const [rawStart, rawEnd] = parts;

  const endMatch = rawEnd.trim().match(/^(\w{3})\s+(\d{4})$/);
  if (!endMatch) return null;
  const endMonth = MONTH_IDX[endMatch[1]];
  const endYear  = parseInt(endMatch[2], 10);

  const startFull  = rawStart.trim().match(/^(\w{3})\s+(\d{4})$/);
  const startShort = rawStart.trim().match(/^(\w{3})$/);

  let startMonth: number;
  let startYear: number;

  if (startFull) {
    startMonth = MONTH_IDX[startFull[1]];
    startYear  = parseInt(startFull[2], 10);
  } else if (startShort) {
    startMonth = MONTH_IDX[startShort[1]];
    startYear  = endYear;
  } else {
    return null;
  }

  const total = (endYear - startYear) * 12 + (endMonth - startMonth);
  if (total <= 0) return null;

  const yrs = Math.floor(total / 12);
  const mos = total % 12;
  if (yrs === 0) return `${mos} mo`;
  if (mos === 0) return `${yrs} yr`;
  return `${yrs} yr ${mos} mo`;
};

const TYPE_CONFIG: Record<EntryType, { color: string; icon: React.ReactNode; label: string }> = {
  work:          { color: '#f59e0b', icon: <FaBriefcase  />, label: 'Work'          },
  education:     { color: '#a78bfa', icon: <FaGraduationCap />, label: 'Education'  },
  certification: { color: '#22c55e', icon: <FaCertificate />, label: 'Certification' },
};

/* ── Styled components ─────────────────────────── */
const AboutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7rem 2rem 5rem;
  background: ${(p) => p.theme.gradients.background};
  min-height: 100vh;
`;

const SectionLabel = styled(motion.div)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.accent};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.22);
  padding: 0.35rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 1rem;
  text-align: center;

  span {
    background: ${(p) => p.theme.gradients.accent};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled(motion.p)`
  color: ${(p) => p.theme.colors.light};
  font-size: 1.05rem;
  max-width: 600px;
  text-align: center;
  margin-bottom: 2rem;
`;

/* ── Bio card ──────────────────────────────────── */
const BioCard = styled(motion.div)`
  background: #0e0e12;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 20px;
  padding: 2rem 2.5rem;
  max-width: 820px;
  width: 100%;
  margin-bottom: 3rem;
  display: flex;
  gap: 2.5rem;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, #7c3aed, #a78bfa, #22c55e);
  }

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
  }
`;

const BioDivider = styled.div`
  width: 1px;
  background: ${(p) => p.theme.colors.glassBorder};
  align-self: stretch;

  @media (max-width: 700px) { width: 100%; height: 1px; }
`;

const BioTextBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const BioName = styled.h2`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.white};
  margin: 0;
`;

const BioRole = styled.p`
  font-size: 0.88rem;
  font-weight: 600;
  background: ${(p) => p.theme.gradients.accent};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const BioText = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.light};
  line-height: 1.7;
  margin: 0;
`;

const BioStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-width: 160px;

  @media (max-width: 700px) {
    flex-direction: row;
    flex-wrap: wrap;
    min-width: 0;
  }
`;

const BioStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const BioStatValue = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  background: ${(p) => p.theme.gradients.accent};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BioStatLabel = styled.span`
  font-size: 0.7rem;
  color: ${(p) => p.theme.colors.muted};
  font-weight: 500;
`;

/* ── Filter tabs ───────────────────────────────── */
const FilterRow = styled(motion.div)`
  display: flex;
  gap: 0.4rem;
  margin-bottom: 1.8rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const FilterTab = styled.button<{ $active: boolean; $color?: string }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;

  ${({ $active, $color, theme }) =>
    $active
      ? `background: ${$color ? $color + '18' : 'rgba(139,92,246,0.15)'};
         color: ${$color ?? theme.colors.accentLight};
         border: 1px solid ${$color ? $color + '40' : 'rgba(139,92,246,0.35)'};`
      : `background: transparent;
         color: ${theme.colors.muted};
         border: 1px solid rgba(255,255,255,0.07);
         &:hover { border-color: rgba(255,255,255,0.15); color: ${theme.colors.white}; }`}
`;

/* ── Accordion list ────────────────────────────── */
const AccordionList = styled.div`
  width: 100%;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 18px;
  overflow: hidden;
`;

const AccordionItem = styled(motion.div)<{ $color: string }>`
  border-bottom: 1px solid rgba(255,255,255,0.05);
  background: #0e0e12;
  transition: background 0.2s ease;

  &:last-child { border-bottom: none; }

  &:hover { background: rgba(255,255,255,0.015); }
`;

const AccordionHeader = styled.button<{ $open: boolean; $color: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  position: relative;

  /* left accent bar */
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: ${(p) => p.$color};
    opacity: ${(p) => (p.$open ? 1 : 0)};
    transition: opacity 0.25s ease;
    border-radius: 0 2px 2px 0;
  }
`;

const IconBadge = styled.span<{ $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: ${(p) => p.$color};
  background: ${(p) => p.$color}14;
  border: 1px solid ${(p) => p.$color}25;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  flex-wrap: wrap;

  @media (max-width: 520px) { flex-direction: column; gap: 0.2rem; }
`;

const EntryTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 650;
  color: ${(p) => p.theme.colors.white};
`;

const EntryDate = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.muted};
`;

const DurationBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.muted};
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const ChevronIcon = styled(motion.span)`
  color: ${(p) => p.theme.colors.muted};
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const AccordionBody = styled(motion.div)`
  overflow: hidden;
`;

const AccordionContent = styled.div<{ $color: string }>`
  padding: 0 1.5rem 1.3rem 4.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;

  @media (max-width: 520px) { padding-left: 1.5rem; }
`;

const EntryDescription = styled.p`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.light};
  line-height: 1.65;
  margin: 0;
`;

const TechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const TechPill = styled.span`
  font-size: 0.65rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.accentLight};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.15);
  padding: 0.18rem 0.55rem;
  border-radius: 8px;
  font-family: ${(p) => p.theme.fonts.code};
`;

/* ── Component ─────────────────────────────────── */
const About: React.FC = () => {
  const { t } = useLang();
  const [filter, setFilter]   = useState<EntryType | 'all'>('all');
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const filters: { key: EntryType | 'all'; label: string; color?: string }[] = [
    { key: 'all',           label: 'All'           },
    { key: 'work',          label: 'Work',          color: '#f59e0b' },
    { key: 'education',     label: 'Education',     color: '#a78bfa' },
    { key: 'certification', label: 'Certifications',color: '#22c55e' },
  ];

  const visible = filter === 'all' ? entries : entries.filter((e) => e.type === filter);

  const toggle = (i: number) => setOpenIdx((prev) => (prev === i ? null : i));

  return (
    <AboutContainer id="about">
      <SectionLabel
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t.about.sectionLabel}
      </SectionLabel>

      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {t.about.title} <span>{t.about.titleHighlight}</span>
      </Title>

      <Subtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {t.about.subtitle}
      </Subtitle>

      {/* Bio */}
      <BioCard
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        <BioStats>
          {(['5+', '30+', '3'] as const).map((val, i) => (
            <BioStatItem key={i}>
              <BioStatValue>{val}</BioStatValue>
              <BioStatLabel>{t.about.statsLabels[i]}</BioStatLabel>
            </BioStatItem>
          ))}
        </BioStats>
        <BioDivider />
        <BioTextBlock>
          <BioName>Igor Chrzyptowicz</BioName>
          <BioRole>{t.about.bioRole}</BioRole>
          <BioText>{t.about.bioText}</BioText>
          <BioText>{t.about.bioLangs}</BioText>
        </BioTextBlock>
      </BioCard>

      {/* Filter tabs */}
      <FilterRow
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
      >
        {filters.map(({ key, label, color }) => (
          <FilterTab
            key={key}
            $active={filter === key}
            $color={color}
            onClick={() => { setFilter(key); setOpenIdx(0); }}
          >
            {key !== 'all' && (
              <span style={{ opacity: 0.85 }}>
                {TYPE_CONFIG[key as EntryType].icon}
              </span>
            )}
            {label}
          </FilterTab>
        ))}
      </FilterRow>

      {/* Accordion */}
      <AccordionList>
        <AnimatePresence initial={false}>
          {visible.map((entry, i) => {
            const cfg   = TYPE_CONFIG[entry.type];
            const open  = openIdx === i;
            return (
              <AccordionItem
                key={`${filter}-${entry.title}-${entry.date}`}
                $color={cfg.color}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25, delay: i * 0.04 }}
              >
                <AccordionHeader $open={open} $color={cfg.color} onClick={() => toggle(i)}>
                  <IconBadge $color={cfg.color}>{cfg.icon}</IconBadge>
                  <HeaderText>
                    <EntryTitle>{entry.title}</EntryTitle>
                    <EntryDate>{entry.date}</EntryDate>
                    {parseDuration(entry.date) && (
                      <DurationBadge>{parseDuration(entry.date)}</DurationBadge>
                    )}
                  </HeaderText>
                  <ChevronIcon
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.22 }}
                  >
                    <FaChevronDown />
                  </ChevronIcon>
                </AccordionHeader>

                <AnimatePresence initial={false}>
                  {open && (
                    <AccordionBody
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: 'easeInOut' }}
                    >
                      <AccordionContent $color={cfg.color}>
                        <EntryDescription>{entry.description}</EntryDescription>
                        {entry.technologies.length > 0 && (
                          <TechRow>
                            {entry.technologies.map((tech) => (
                              <TechPill key={tech}>{tech}</TechPill>
                            ))}
                          </TechRow>
                        )}
                      </AccordionContent>
                    </AccordionBody>
                  )}
                </AnimatePresence>
              </AccordionItem>
            );
          })}
        </AnimatePresence>
      </AccordionList>
    </AboutContainer>
  );
};

export default About;
